/**
 * PhotoGallery Component
 *
 * Grid display of listing photos with modal viewer.
 */

import React, { useState } from 'react';
import { PhotoModal } from './PhotoModal';
import type { ListingPhoto } from '../hooks/useListing';

interface PhotoGalleryProps {
  photos: ListingPhoto[];
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openModal = (index: number) => {
    setActiveIndex(index);
    setIsModalOpen(true);
  };

  if (photos.length === 0) {
    return (
      <div className="aspect-[16/9] bg-gray-200 rounded-xl flex items-center justify-center">
        <span className="text-gray-500">No photos available</span>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 rounded-xl overflow-hidden">
        {/* Primary Image */}
        <button
          className="md:col-span-2 md:row-span-2 cursor-pointer overflow-hidden relative group"
          onClick={() => openModal(0)}
        >
          <img
            src={photos[0].url}
            alt={photos[0].caption || 'Primary photo'}
            className="w-full h-full object-cover aspect-[4/3] md:aspect-auto md:h-full group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </button>

        {/* Secondary Images */}
        {photos.slice(1, 5).map((photo, idx) => (
          <button
            key={idx}
            className="hidden md:block cursor-pointer overflow-hidden relative group"
            onClick={() => openModal(idx + 1)}
          >
            <img
              src={photo.url}
              alt={photo.caption || `Photo ${idx + 2}`}
              className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

            {/* More photos indicator */}
            {idx === 3 && photos.length > 5 && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-semibold">
                  +{photos.length - 5} more
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Show all photos button (mobile) */}
      <button
        onClick={() => openModal(0)}
        className="md:hidden mt-2 w-full py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
      >
        Show all {photos.length} photos
      </button>

      {/* Photo Modal */}
      <PhotoModal
        photos={photos}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        activeIndex={activeIndex}
        onIndexChange={setActiveIndex}
      />
    </>
  );
};

export default PhotoGallery;
